import { Copy } from "../helper/clipboard-helper";
import { CheckResultData, ResultLevel } from "./checkresultdata";
import { Logger } from "../logger/logger";
import { ServiceProvider } from "../core/provider/service-provider";
import { DateHelper } from "../helper/date-helper";
import { LoadVm } from "../loading/loadvm";
import { ErrorHelper } from "../core/error";
import { Lazy } from "../core/lazy";
import { UrlHelper } from "../core/html/url-helper";
import { MessageHelper } from "../helper/message-helper";
import { dx } from "../devexpress/ui-options-types-wrapper";
import { E2EHelper } from "../core/e2e-helper";
import { KoExtend } from "../extend/knockout-extend";
import { ConnectionInformationVm } from "../version/connection-information-vm";
import { AjaxParams, IAjaxOptions } from "../core/provider/provider-interfaces";
import { CheckResultListItem } from "./check-result-list-item";

export enum DialogActionResult {
	None = 0,
	Ok = 1,
	Cancel = 2,
	Ignored = 3,
	Error = 4
}

export interface IDialogRequestOptions extends IAjaxOptions {
	title?: string;
	errorMessage?: string;
	returnCheckResultData?: boolean;
	suppressDialog?: boolean;
	showInfo?: boolean;
}

export class DialogResultVm {
	public static getInstance = Lazy.singleton(DialogResultVm);
	public static newLineSeparator = "^";
	public static showErrorDialog = (title: string, messages: string[] | Error, callback?: () => void) => {
		let data = new CheckResultData();
		data.title = title;
		data.success = false;

		if (messages instanceof Error) {
			data.errorList = [messages.name, messages.message, messages.stack];
		} else {
			data.errorList = messages;
		}

		data.highestLevel = ResultLevel.Error;
		data.checkResultList = [];
		let cancelFn = callback || jQuery.noop;
		DialogResultVm.showDialog(data, cancelFn(), cancelFn());
	}

	public static closeDialogIfOpen = () => {
		let currentDialog = DialogResultVm.getInstance();
		let isAlreadyShown = currentDialog.popupIsVisible();
		if (isAlreadyShown) {
			// cancel old dialog
			currentDialog.onCancel();
			currentDialog.dialogRegularClosed(true);
			currentDialog.popupIsVisible(false);
		}
	}

	public static initDialogAndAssignCheckResultData = (data: CheckResultData) => {
		let currentDialog = DialogResultVm.getInstance();
		currentDialog.init();
		currentDialog.title(`${data.title || "Es ist ein Fehler aufgetreten"} | ${DateHelper.datetimeToGermanZeroLeadingString(data.resultTime || DateHelper.now())}`);
		currentDialog.checkResultList(data.checkResultList);
		currentDialog.highestLevel(data.highestLevel);
		currentDialog.errorList(data.errorList);
		currentDialog.resultTime(data.resultTime);
		currentDialog.error(data.error);
		currentDialog.exception(data.exception);
	}

	public static showDialog = (data: CheckResultData, onSuccess?: any, onCancel?: any) => {
		if (data != null) {
			let currentDialog = DialogResultVm.getInstance();
			DialogResultVm.closeDialogIfOpen();
			DialogResultVm.initDialogAndAssignCheckResultData(data);
			if (onSuccess != null) {
				currentDialog.onSuccess = onSuccess;
			}

			if (onCancel != null) {
				currentDialog.onCancel = onCancel;
			}

			currentDialog.popupIsVisible(true);
		}
	}

	public static showDialogAndGetResultAsync = async (data: CheckResultData): Promise<DialogActionResult> => {
		if (data == null) {
			return DialogActionResult.Ignored;
		}
		DialogResultVm.closeDialogIfOpen();
		DialogResultVm.initDialogAndAssignCheckResultData(data);

		// NOTE(jpg): workaround for z-index bug with multiple devextreme dialogs and load vm
		let wasLoading = LoadVm.getInstance().isVisible();
		LoadVm.getInstance().isVisible(false);

		let currentDialog = DialogResultVm.getInstance();
		return new Promise((resolve: (result: DialogActionResult) => void) => {
			currentDialog.onSuccess = () => {
				LoadVm.getInstance().isVisible(wasLoading);
				resolve(DialogActionResult.Ok);
			};
			currentDialog.onCancel = () => {
				LoadVm.getInstance().isVisible(wasLoading);
				resolve(DialogActionResult.Cancel);
			};
			currentDialog.popupIsVisible(true);
		});
	}

	/**
	 * NOTE(jpg): The returned Promise gets resolved, if the request(s) succeeded, but never rejected if the request(s) failed.
	 * That means after await, the process always succeeded. To handle an error use the onError callback.
	 */
	public static createLoadableCheckResultProcessAsync = (args: {
		loadingTitle?: string,
		isHigherOverlay?: boolean,
		isRunningFlag?: KnockoutObservable<boolean>,
		canRequest?: { url: string, params: AjaxParams, continueOnMaxLevel?: ResultLevel },
		doRequest: { url: string, params: AjaxParams },
		title: string,
		errorMessage?: string,
		onError?: (err: any) => any,
		method?: string,
	}): Promise<CheckResultData> => {
		let isRunning = args.isRunningFlag || ko.observable(false);
		let failMessage = args.errorMessage || "Es ist ein Fehler aufgetreten.";
		let canReq = args.canRequest;
		let doReq = args.doRequest;
		let method: any = args.method || "GET";
		if (canReq) {
			canReq.continueOnMaxLevel = canReq.continueOnMaxLevel !== undefined
				? canReq.continueOnMaxLevel
				: ResultLevel.Info;
		}

		return new Promise<CheckResultData>((accept) => {
			if (isRunning()) {
				return;
			}

			isRunning(true);
			LoadVm.startLoading({ text: args.loadingTitle, isHigherOverlay: args.isHigherOverlay });

			let alwaysFn = () => {
				isRunning(false);
				LoadVm.endLoading();
			};

			let successFn = (data: CheckResultData) => {
				alwaysFn();
				accept(data);
			};

			let cancelFn = () => {
				alwaysFn();
				accept(null);
			};

			let failFn = (err: any) => {
				alwaysFn();
				Logger.warn("Fail on do request:", err);
				// if httpStatusCode != 200 but still valid CheckResult
				if (err && err.responseJSON && err.responseJSON.success === false) {
					DialogResultVm.showDialog(err.responseJSON);
				} else {
					let afterErrorDialog = args.onError && (() => { args.onError(err); });
					DialogResultVm.showErrorDialog(args.title, [`${failMessage}\n${ErrorHelper.toString(err)}`], afterErrorDialog);
				}
				if (err && err.responseJSON) {
					accept(err.responseJSON);
				}
				accept(null);
			};

			let doFn = () => {
				ServiceProvider.ajax.jsonAsync({ method, url: doReq.url, params: doReq.params })
					.catch(failFn)
					.then((data: CheckResultData) => {
						if (data && data.highestLevel === 0) {
							data.title = args.title;
							successFn(data);
						} else {
							Logger.warn("Do needs attention:", data);
							data.title = args.title;
							LoadVm.endLoading(); // pause loading to show Dialog if isHigherOverlay
							DialogResultVm.showDialog(data, (successData: CheckResultData) => {
								LoadVm.startLoading({ text: args.loadingTitle, isHigherOverlay: args.isHigherOverlay });
								successFn(successData);
							}, cancelFn);
						}
					});
			};

			let canFn = () => {
				ServiceProvider.ajax.jsonAsync({ method, url: canReq.url, params: canReq.params })
					.catch(failFn)
					.then((data: CheckResultData) => {
						// can successful, check level
						if (data && data.success && data.highestLevel <= canReq.continueOnMaxLevel) {
							// call doUrl
							data.title = args.title;
							doFn();
						} else {
							Logger.warn("Can needs attention");
							data.title = args.title;
							LoadVm.endLoading(); // pause loading to show Dialog if isHigherOverlay
							DialogResultVm.showDialog(data, () => {
								LoadVm.startLoading({ text: args.loadingTitle, isHigherOverlay: args.isHigherOverlay });
								doFn();
							}, cancelFn);
						}
					});
			};

			if (canReq) {
				canFn();
			} else {
				doFn();
			}
		});
	}

	public static getDataWithWarningDialogRetryAsync = async (args: IDialogRequestOptions): Promise<any> => {
		args.params = args.params || {};
		args.params.ignoreWarnings = args.params.ignoreWarnings || false;

		let result: CheckResultData = await ServiceProvider.ajax.jsonAsync(args).catch((err) => {
			Logger.error(`Error while getting checkresultdata`, { args, err });
			if (!err) {
				return null;
			}

			return err.responseJSON;
		});

		if (!result) {
			MessageHelper.uiError(args.errorMessage);
			return args.returnCheckResultData ? result : null;
		}
		if (!result.success) {
			if (!args.suppressDialog) {
				await DialogResultVm.showDialogAndGetResultAsync(result);
			}
			return args.returnCheckResultData ? result : null;
		}

		if (result.highestLevel === ResultLevel.Warning) {
			if (args.title) {
				result.title = args.title;
			}
			let dialogResult = args.suppressDialog ? DialogActionResult.Ok : await DialogResultVm.showDialogAndGetResultAsync(result);
			if (dialogResult === DialogActionResult.Ok) {
				args.params.ignoreWarnings = true;
				let ignoreWarningResult: CheckResultData = await ServiceProvider.ajax.jsonAsync({
					url: args.url,
					params: args.params,
					method: args.method || "GET"
				}).catch((err) => {
					Logger.error(`Error while getting checkresultdata with ignoreWarnings`, { args, err });
					if (!err) {
						return null;
					}

					return err.responseJSON;
				});

				if (!ignoreWarningResult) {
					return null;
				}
				if (!ignoreWarningResult.success) {
					if (args.title) {
						ignoreWarningResult.title = args.title;
					}

					if (!args.suppressDialog) {
						await DialogResultVm.showDialogAndGetResultAsync(ignoreWarningResult);
					}
					return args.returnCheckResultData ? ignoreWarningResult : null;
				}
				return args.returnCheckResultData ? ignoreWarningResult : ignoreWarningResult.resultData;
			}
		} else {
			args.showInfo = args.showInfo || false;
			if (args.showInfo && result.checkResultList && result.checkResultList.some((item) => item.resultLevel === ResultLevel.Info)) {
				result.title = args.title ?? result.title;
				await DialogResultVm.showDialog(result);
			}
			return args.returnCheckResultData ? result : result.resultData;
		}
		return args.returnCheckResultData ? result : null;
	}

	constructor() {
		this.title = ko.observable("");
		this.popupIsVisible = ko.observable(false);
		this.dialogResultSearchValue = ko.observable("");
		this.ignoreWarningChecked = ko.observable(false);
		this.checkResultList = ko.observableArray([]);
		this.resultTime = ko.observable();
		this.highestLevel = ko.observable(0);
		this.errorList = ko.observableArray([]);
		this.error = ko.observable(null);
		this.exception = ko.observable(null);
		this.onSuccess = () => { MessageHelper.uiError("Es wurde keine Aktion hinterlegt"); };
		this.onCancel = $.noop;
		this.showIgnoreWarning = ko.computed(() => this.highestLevel() === ResultLevel.Warning);
		this.checkDisableOk = ko.computed(() => !(this.highestLevel() <= ResultLevel.Info || this.ignoreWarningChecked()));
		this.checkOkVisible = ko.computed(() => {
			let highestLevel = this.highestLevel();
			return highestLevel < ResultLevel.Error;
		});
		this.checkCancelDisabled = ko.computed(() => {
			let ignoreWarnings = this.ignoreWarningChecked();
			return ignoreWarnings;
		});
		this.popupOptions = {
			width: 650,
			height: "auto",
			maxHeight: "90%",
			wrapperAttr: {
				class:"dialog-popup"
			},
			showTitle: true,
			title: this.title,
			visible: this.popupIsVisible,
			dragEnabled: true,
			hideOnOutsideClick: false,
			showCloseButton: false,
			toolbarItems: [{
				toolbar: "top",
				location: "after",
				widget: "dxButton",
				options: {
					type: "close",
					icon: "close",
					onClick: () => {
						this.dialogRegularClosed(true);
						this.popupIsVisible(false);
						this.onCancel();
					}
				}
			}],
			onHiding: (event: any) => {
				if (!this.dialogRegularClosed()) {
					event.cancel = true;
					this.popupIsVisible(true);
				}
			}
		};

		this.toggleIgnoreWarningChecked = () => {
			this.ignoreWarningChecked(!this.ignoreWarningChecked());
		};

		this.checkResultDialogOkOptions = {
			disabled: this.checkDisableOk,
			onClick: () => {
				this.dialogRegularClosed(true);
				this.popupIsVisible(false);
				this.onSuccess();
			},
			visible: this.checkOkVisible,
		};

		this.checkResultDialogCancelOptions = {
			onClick: () => {
				this.dialogRegularClosed(true);
				this.popupIsVisible(false);
				this.onCancel();
			},
			disabled: this.checkCancelDisabled,
			visible: ko.pureComputed(() => this.highestLevel() !== ResultLevel.Info),
		};

		this.continueWithWarningOptions = {
			value: this.ignoreWarningChecked
		};

		this.showText = (data: any) => {
			if (data !== "undefined" &&
				data.items &&
				data.items.length > 0 &&
				this.dialogResultSearchValue() === "") {
				return `${data.text} (${data.items.length})`;
			}
			return data.text;
		};

		this.showTextArray = (data: any) => {
			let result: string[] = [];
			let text = this.showText(data);
			if (text) {
				result = text.split(DialogResultVm.newLineSeparator);
			}
			return result;
		};

		this.treeViewOptions = {
			noDataText: "",
			items: this.checkResultList,
			searchValue: this.dialogResultSearchValue
		};

		this.messages = ko.computed(() => {
			let curResultList = this.checkResultList();
			let tmpMessages = "";
			if (curResultList && curResultList.length > 0) {
				tmpMessages += "Ermittelte Meldungen:\r\n";
				ko.utils.arrayForEach(this.checkResultList(), (listEntry: any) => {
					if (listEntry != null && listEntry.items != null && listEntry.items.length > 0) {
						for (let listEntryItem of listEntry.items) {
							tmpMessages += `- ${listEntry.text}: ${listEntryItem.text}\r\n`;
						}
					}
				});
				tmpMessages += `\r\n`;
			}

			let curErrors = this.errorList();
			if (curErrors && curErrors.length > 0) {
				tmpMessages += `Sonstige Fehler:\r\n`;
				ko.utils.arrayForEach(curErrors, (listEntry: any) => {
					if (listEntry != null && listEntry.length > 0) {
						tmpMessages += `- ${listEntry}\r\n`;
					}
				});
				tmpMessages += "\r\n";
			}

			let connectionInfo = ConnectionInformationVm.getInstance();
			tmpMessages += `Clientversion: ${UrlHelper.getData("data-client-version")}\r\n`;
			tmpMessages += `Datenbankversion: ${connectionInfo.catalogVersion()}\r\n`;
			tmpMessages += `Umgebung: Webserver=${connectionInfo.webServer()}, `;
			tmpMessages += `Server=${connectionInfo.datasource()}, `;
			tmpMessages += `Datenbank=${connectionInfo.catalog()}, `;
			tmpMessages += `Benutzer=${connectionInfo.user()}, `;
			tmpMessages += `Vermarkter=${connectionInfo.marketer()} (${connectionInfo.marketerKey()})\r\n`;
			tmpMessages += `Browserlink: ${UrlHelper.getCurrentUrl()}\r\n`;
			tmpMessages += `UserAgent: ${navigator.userAgent}\r\n`;
			tmpMessages += `Gesch\u00e4ftsobjekt: ${this.title()}\r\n`;
			tmpMessages += `Client Fehlerzeit: ${DateHelper.datetimeToGermanZeroLeadingStringOrDefault(DateHelper.nowAsDateWithTime())}\r\n`;
			tmpMessages += `Server Fehlerzeit: ${DateHelper.datetimeToGermanZeroLeadingStringOrDefault(this.resultTime())}\r\n`;

			let error = this.error();
			if (error?.stack) {
				tmpMessages += `Stacktrace: ${error.stack}\r\n`;
			}

			let exception = this.exception();
			if (exception) {
				tmpMessages += `Exception: ${exception}\r\n`;
			}

			return tmpMessages;
		});

		this.download = () => {
			let messages = this.messages();
			let blob = new Blob([messages], { type: "text/plain" });
			let filename = `Nautilus_Fehlerbericht_${DateHelper.fullDateToFilename()}.txt`;
			let elem = window.document.createElement("a");
			elem.href = window.URL.createObjectURL(blob);
			elem.download = filename;
			document.body.appendChild(elem);
			elem.click();
			document.body.removeChild(elem);
		};

		this.copyToClipboard = () => {
			// copy all check resuls to clipboard...
			let messages = this.messages();

			if (!Copy.copyTextToClipboard(messages)) {
				MessageHelper.uiError("Die Daten konnten nicht in die Zwischenablage kopiert werden");
			} else {
				MessageHelper.uiSuccess("Die Daten wurden erfolgreich in die Zwischenablage kopiert");
			}
		};

		this.searchOptions = {
			value: this.dialogResultSearchValue,
			placeholder: "Filter...",
			width: 300,
			mode: "search",
			valueChangeEvent: "keyup"
		};

		this.init = () => {
			this.dialogResultSearchValue("");
			this.ignoreWarningChecked(false);
			this.onSuccess = () => { };
			this.onCancel = () => { };
			this.dialogRegularClosed(false);
		};

		KoExtend.subscribeBeforeAfter(this.popupIsVisible, (oldVisible, newVisible) => {
			if (!oldVisible && newVisible) {
				ConnectionInformationVm.getInstance().updateAsync();
				E2EHelper.getInstance().trigger({
					source: "dialog",
					checkResultList: this.checkResultList(),
					errorList: this.errorList(),
					severity: this.highestLevel(),
					message: this.messages(),
				});
			}
		});

		this.checkResultList.subscribe((items) => {
			if (!items) {
				return;
			}

			for (let item of items) {
				if (!item || item.resultLevel < ResultLevel.Error) {
					continue;
				}
				item.expanded = true;
			}
		});
	}

	public error: KnockoutObservable<any>;
	public title: KnockoutObservable<string>;
	public exception: KnockoutObservable<string>;
	public popupIsVisible: KnockoutObservable<boolean>;
	public dialogResultSearchValue: KnockoutObservable<string>;
	public ignoreWarningChecked: KnockoutObservable<boolean>;
	public toggleIgnoreWarningChecked: () => void;
	public checkResultList: KnockoutObservableArray<CheckResultListItem>;
	public highestLevel: KnockoutObservable<number>;
	public errorList: KnockoutObservableArray<string>;
	public resultTime: KnockoutObservable<Date>;
	public messages: KnockoutComputed<string>;
	public onSuccess: (data?: CheckResultData) => void;
	public showIgnoreWarning: KnockoutComputed<boolean>;
	public checkDisableOk: KnockoutComputed<boolean>;
	public dialogRegularClosed: KnockoutObservable<boolean> = ko.observable(true);
	public popupOptions: dx.PopupOptions;
	public checkResultDialogOkOptions: dx.ButtonOptions;
	public checkResultDialogCancelOptions: dx.ButtonOptions;
	public continueWithWarningOptions: dx.CheckBoxOptions;
	public treeViewOptions: dx.TreeViewOptions;
	public searchOptions: dx.TextBoxOptions;
	public showText: (data: any) => string;
	public showTextArray: (data: any) => string[];
	public copyToClipboard: () => void;
	public download: () => void;
	public init: () => void;

	public checkCancelDisabled: KnockoutComputed<boolean>;
	public checkOkVisible: KnockoutComputed<boolean>;
	protected onCancel: () => void;
}